import React from "react";
import { Link } from "react-router-dom";
import { SeoHero, TextSection } from "../../pages/seo/SeoPageBlocks";
import { RevealGroup, RevealItem, RevealStagger } from "../../components/motion/Reveal";
import { PUBLIC_MOTION } from "../../components/motion/publicMotion";

const segmentLinks = [
  {
    to: "/klassforsaljning",
    label: "För skolklasser",
    text: "Klassförsäljning för klassresa och gemensamma aktiviteter.",
  },
  {
    to: "/tjana-pengar-till-laget",
    label: "För idrottslag",
    text: "Försäljning till cup, läger, utrustning och lagkassa.",
  },
  {
    to: "/tjana-pengar-till-foreningen",
    label: "För föreningar",
    text: "Upplägg för föreningens lag och grupper.",
  },
];

const startLinks = [
  { to: "/register-group", label: "Registrera er grupp" },
  { to: "/vanliga-fragor", label: "Vanliga frågor" },
];

function TextLink({ to, children }) {
  return (
    <Link
      to={to}
      className="font-medium text-amber-800 underline decoration-amber-300 underline-offset-2 hover:text-amber-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
    >
      {children}
    </Link>
  );
}

const process = [
  {
    title: "Registrera gruppen",
    text: "En vuxen klass- eller gruppledare registrerar klassen eller laget och anger grupp samt planerad försäljningsperiod. Registreringen är en ansökan och behöver godkännas. Efter godkännande fortsätter onboarding.",
  },
  {
    title: "Bjud in säljarna",
    text: "Gruppledaren bjuder in säljarna så att deltagarna kan komma igång med försäljningen.",
  },
  {
    title: "Varje säljare får sin länk",
    text: "Varje säljare får en personlig säljlänk och QR-kod. Beställningar via länken kopplas till rätt säljare och grupp.",
  },
  {
    title: "Kunden beställer",
    text: "Kunden öppnar säljlänken och ser de betalningsalternativ som är tillgängliga i kassan. Direktbetalning hanteras genom Stripe. Säljaren behöver inte hantera kontanter.",
  },
  {
    title: "Ordern levereras",
    text: (
      <>
        Ordern kan levereras direkt till kunden eller som gruppleverans när det alternativet är
        tillgängligt för beställningen.{" "}
        <TextLink to="/leveransvillkor">Läs leveransvillkoren</TextLink>.
      </>
    ),
  },
  {
    title: "Försäljningen avslutas",
    text: "Kampanjen stängs vid periodens slut.",
  },
  {
    title: "Förtjänsten räknas",
    text: (
      <>
        Provisionen är produkt- och variantbunden och kan variera. Faktisk förtjänst baseras på den
        faktiska provisionen på sålda produkter. Returer och återbetalningar kan påverka
        slutbeloppet.{" "}
        <TextLink to="/provision-och-fortjanst">Räkna på förtjänsten</TextLink>.
      </>
    ),
  },
  {
    title: "Utbetalning",
    text: "Efter att alla order levererats och 14 dagars väntetid passerat kan förtjänsten betalas ut.",
  },
];

export default function SaFungerarContent() {
  return (
    <div className="bg-slate-50">
      <SeoHero
        eyebrow="Hela flödet"
        title="Så fungerar SalesPartner – steg för steg"
        description="SalesPartner samlar gruppens försäljning i ett digitalt flöde från registrering till leverans och utbetalning."
        primaryCtaLabel="Registrera er grupp"
        primaryCtaTo="/register-group"
        secondaryCtaLabel="Vanliga frågor"
        secondaryCtaTo="/vanliga-fragor"
      />

      <section className="bg-white py-12 md:py-16">
        <RevealGroup className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealItem tone="text" as="h2" className="text-2xl md:text-3xl font-bold text-slate-900">
            Från registrering till utbetalning
          </RevealItem>
          <RevealStagger as="ol" className="mt-8 grid gap-5 md:grid-cols-2" stagger={PUBLIC_MOTION.staggerProcess}>
            {process.map((item, index) => (
              <RevealItem key={item.title} as="li" tone="card" y={30} blur={4}>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:p-6 transition duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md">
                  <div className="flex items-start gap-4">
                    <span
                      aria-hidden="true"
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-950 font-bold text-amber-300"
                    >
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                      <p className="mt-2 text-slate-700 leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </RevealGroup>
      </section>

      <TextSection
        eyebrow="Säljaren"
        title="Så fungerar det för säljaren"
        intro="Säljaren delar sin länk. Beställningen kopplas automatiskt."
        tone="muted"
      >
        <p>
          Varje säljare får en personlig säljlänk och QR-kod och delar den med kunder. Beställningar
          via länken kopplas till rätt säljare och grupp. Säljaren behöver inte hantera kontanter.
        </p>
      </TextSection>

      <TextSection
        eyebrow="Kunden"
        title="Så fungerar det för kunden"
        intro="Kunden handlar via säljlänken och ser de val som faktiskt finns för ordern."
      >
        <p>
          Kunden öppnar säljlänken, väljer ur aktuellt sortiment och genomför beställningen. I
          kassan visas de betalningsalternativ som är tillgängliga. Leveransen följer det
          upplägg som är valt och tillgängligt för ordern.
        </p>
        <p>
          <TextLink to="/products">Se aktuellt sortiment</TextLink>
        </p>
      </TextSection>

      <TextSection
        eyebrow="Två leveransvägar"
        title="Direkt till kunden eller gemensamt till gruppen"
        tone="muted"
      >
        <p>
          Vid direktleverans skickas beställningen till kunden. Normal direktleverans är 3–5
          arbetsdagar efter betalning och bekräftad order.
        </p>
        <p>
          Vid gruppleverans skickas gruppens beställningar till den angivna gruppadressen och
          gruppledaren ansvarar för utdelningen. Tidpunkten för gruppleverans kan variera.
        </p>
        <p>
          Gruppfaktura tillsammans med gruppleverans kan finnas när det är tillgängligt för ordern.
          De val som faktiskt kan användas visas i kassan.{" "}
          <TextLink to="/leveransvillkor">Läs leveransvillkoren</TextLink>.
        </p>
      </TextSection>

      <section className="bg-white py-12 md:py-16 border-b border-slate-100">
        <RevealGroup className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealItem tone="text">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Välj rätt upplägg för er grupp</h2>
          </RevealItem>
          <RevealItem tone="text">
            <p className="mt-4 max-w-3xl text-slate-700 leading-relaxed">
              Själva flödet är detsamma. Klass, lag och förening har egna sidor med mer om respektive
              upplägg.
            </p>
          </RevealItem>
          <RevealStagger className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" stagger={PUBLIC_MOTION.staggerCard}>
            {segmentLinks.map((link) => (
              <RevealItem key={link.to} tone="card">
                <Link
                  to={link.to}
                  className="block rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-800 transition hover:border-amber-300 hover:bg-white hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
                >
                  <span className="font-medium">{link.label}</span>
                  <p className="mt-2 font-normal text-slate-700 leading-relaxed">{link.text}</p>
                </Link>
              </RevealItem>
            ))}
          </RevealStagger>
        </RevealGroup>
      </section>

      <TextSection
        eyebrow="Nästa steg"
        title="Kom igång"
        intro="Registrera gruppen eller läs svaren på vanliga frågor."
      >
        <div className="grid sm:grid-cols-2 gap-4">
          {startLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-5 font-medium text-slate-800 transition hover:border-amber-300 hover:bg-white hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </TextSection>
    </div>
  );
}
